import { Box, Typography, alpha } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { DATA_ATTRS, PRIMARY_ALPHA } from '../ColumnConfiguratorDrawer.styles';

export interface ColumnPickerItemProps {
  columnKey: string;
  label: string;
  isVisible: boolean;
  isDisabled: boolean;
  primary: string;
  onToggle: (key: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>, key: string) => void;
}

export function ColumnPickerItem({
  columnKey,
  label,
  isVisible,
  isDisabled,
  primary,
  onToggle,
  onKeyDown,
}: ColumnPickerItemProps) {
  return (
    <Box
      component="button"
      type="button"
      role="checkbox"
      aria-checked={isVisible}
      aria-label={label}
      aria-disabled={isDisabled}
      {...{ [DATA_ATTRS.columnKey]: columnKey }}
      onClick={() => !isDisabled && onToggle(columnKey)}
      onKeyDown={e => onKeyDown(e, columnKey)}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1.125,
        borderRadius: 'var(--border-radius)',
        border: '1.5px solid',
        borderColor: isVisible ? alpha(primary, PRIMARY_ALPHA.itemBorder) : 'var(--gray-200)',
        bgcolor: isVisible ? alpha(primary, PRIMARY_ALPHA.itemBg) : 'white',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
        textAlign: 'left',
        outline: 'none',
        opacity: isDisabled ? 0.45 : 1,
        boxShadow: isVisible
          ? `0 1px 4px ${alpha(primary, PRIMARY_ALPHA.itemShadow)}, 0 1px 2px var(--shadow-4)`
          : '0 1px 2px var(--shadow-4)',
        '&:hover:not(:disabled)': {
          borderColor: isVisible ? alpha(primary, PRIMARY_ALPHA.itemBorderHover) : 'var(--gray-350)',
          bgcolor: isVisible ? alpha(primary, PRIMARY_ALPHA.itemBgHover) : 'var(--gray-100)',
          transform: 'translateY(-1px)',
          boxShadow: isVisible
            ? `0 4px 10px ${alpha(primary, PRIMARY_ALPHA.itemShadowHover)}`
            : '0 2px 6px var(--shadow-8)',
        },
        '&:active:not(:disabled)': { transform: 'translateY(0)', transition: 'transform 0.08s' },
        '&:focus-visible': { outline: `2px solid ${primary}`, outlineOffset: 1 },
      }}
    >
      {/* Checkbox */}
      <Box
        sx={{
          width: 'var(--checkbox-size)',
          height: 'var(--checkbox-size)',
          borderRadius: 'var(--checkbox-radius)',
          border: '2px solid',
          borderColor: isVisible ? primary : 'var(--gray-300)',
          bgcolor: isVisible ? primary : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {isVisible && <CheckIcon sx={{ fontSize: 'var(--icon-checkbox)', color: 'white' }} />}
      </Box>

      {/* Label */}
      <Typography
        sx={{
          flex: 1,
          fontWeight: isVisible ? 600 : 400,
          color: isVisible ? 'var(--gray-900)' : 'var(--gray-500)',
          fontSize: 'var(--font-item)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          letterSpacing: isVisible ? '-0.01em' : 'normal',
        }}
      >
        {label}
      </Typography>

      {/* Visibility icon */}
      <Box
        sx={{
          flexShrink: 0,
          color: isVisible ? primary : 'var(--gray-300)',
          display: 'flex',
          opacity: isVisible ? 1 : 0.6,
          transition: 'color 0.15s',
        }}
      >
        {isVisible ? (
          <VisibilityIcon sx={{ fontSize: 'var(--icon-eye)' }} />
        ) : (
          <VisibilityOffIcon sx={{ fontSize: 'var(--icon-eye)' }} />
        )}
      </Box>
    </Box>
  );
}
